// ============================================================
// rank-badge.ts — Rank tier badge with rating display
// DOM-based component, dark military theme.
// ============================================================

import { isMobile } from '../../core/device';

const ACCENT = '#e94560';

// ── Tier definitions ────────────────────────────────────────

export type RankTier = 'Bronze' | 'Silver' | 'Gold' | 'Platinum' | 'Diamond' | 'Champion';

interface TierDef {
  name: RankTier;
  minRating: number;
  color: string;
  icon: string; // Unicode symbol
}

const TIERS: TierDef[] = [
  { name: 'Champion', minRating: 2400, color: '#FF4500', icon: '\u2605' },   // black star
  { name: 'Diamond',  minRating: 2100, color: '#B9F2FF', icon: '\u25C6' },   // diamond
  { name: 'Platinum', minRating: 1800, color: '#00CED1', icon: '\u25B2' },   // triangle up
  { name: 'Gold',     minRating: 1500, color: '#FFD700', icon: '\u2736' },   // six-pointed star
  { name: 'Silver',   minRating: 1200, color: '#C0C0C0', icon: '\u25CF' },   // circle
  { name: 'Bronze',   minRating: 0,    color: '#CD7F32', icon: '\u25A0' },   // square
];

// ── Style injection ─────────────────────────────────────────

let stylesInjected = false;

function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;

  const style = document.createElement('style');
  style.textContent = `
    @keyframes rb-slide-up {
      0% { transform: translateY(8px); opacity: 0; }
      100% { transform: translateY(0); opacity: 1; }
    }
    @keyframes rb-slide-down {
      0% { transform: translateY(-8px); opacity: 0; }
      100% { transform: translateY(0); opacity: 1; }
    }

    .rank-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 6px 14px;
      background: rgba(0,0,0,0.5);
      border: 1px solid #333;
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      user-select: none;
    }

    .rank-badge-icon {
      font-size: 20px;
      text-shadow: 0 0 8px currentColor;
      line-height: 1;
    }

    .rank-badge-info {
      display: flex;
      flex-direction: column;
      gap: 1px;
    }

    .rank-badge-tier {
      font-size: 11px;
      font-weight: bold;
      letter-spacing: 2px;
      text-transform: uppercase;
    }

    .rank-badge-rating {
      font-size: 16px;
      font-weight: bold;
      color: #fff;
      letter-spacing: 1px;
      position: relative;
      overflow: hidden;
    }

    .rank-badge-change {
      font-size: 12px;
      font-weight: bold;
      letter-spacing: 1px;
      margin-left: 6px;
      display: inline-block;
    }
    .rank-badge-change.positive {
      color: #44ff44;
      animation: rb-slide-up 0.4s ease-out;
    }
    .rank-badge-change.negative {
      color: #ff4444;
      animation: rb-slide-down 0.4s ease-out;
    }
  `;
  document.head.appendChild(style);
}

// ── Public API ──────────────────────────────────────────────

/** Determine the rank tier for a given rating. */
export function getRankTier(rating: number): RankTier {
  for (const tier of TIERS) {
    if (rating >= tier.minRating) return tier.name;
  }
  return 'Bronze';
}

function getTierDef(tier: RankTier): TierDef {
  return TIERS.find((t) => t.name === tier) ?? TIERS[TIERS.length - 1];
}

/** Create a rank badge DOM element. */
export function createRankBadge(rating: number, tier?: RankTier): HTMLElement {
  injectStyles();

  const resolvedTier = tier ?? getRankTier(rating);
  const def = getTierDef(resolvedTier);

  const badge = document.createElement('div');
  badge.className = 'rank-badge';
  badge.style.borderColor = def.color;
  if (isMobile()) {
    badge.style.transform = 'scale(0.8)';
    badge.style.transformOrigin = 'left center';
  }
  badge.dataset.rating = String(rating);
  badge.dataset.tier = resolvedTier;

  // Icon
  const icon = document.createElement('span');
  icon.className = 'rank-badge-icon';
  icon.style.color = def.color;
  icon.textContent = def.icon;
  badge.appendChild(icon);

  // Info column
  const info = document.createElement('div');
  info.className = 'rank-badge-info';

  const tierLabel = document.createElement('div');
  tierLabel.className = 'rank-badge-tier';
  tierLabel.style.color = def.color;
  tierLabel.textContent = resolvedTier;
  info.appendChild(tierLabel);

  const ratingEl = document.createElement('div');
  ratingEl.className = 'rank-badge-rating';

  const ratingNum = document.createElement('span');
  ratingNum.className = 'rank-badge-rating-num';
  ratingNum.textContent = String(rating);
  ratingEl.appendChild(ratingNum);

  const changeSpan = document.createElement('span');
  changeSpan.className = 'rank-badge-change';
  changeSpan.style.display = 'none';
  ratingEl.appendChild(changeSpan);

  info.appendChild(ratingEl);
  badge.appendChild(info);

  return badge;
}

/** Update a rank badge with a new rating and animate the change. */
export function updateRankBadge(el: HTMLElement, newRating: number, change: number): void {
  const def = getTierDef(getRankTier(newRating));

  // Update data attributes
  el.dataset.rating = String(newRating);
  el.dataset.tier = def.name;

  // Update border color
  el.style.borderColor = def.color;

  // Update icon
  const icon = el.querySelector('.rank-badge-icon') as HTMLElement | null;
  if (icon) {
    icon.style.color = def.color;
    icon.textContent = def.icon;
  }

  // Update tier label
  const tierLabel = el.querySelector('.rank-badge-tier') as HTMLElement | null;
  if (tierLabel) {
    tierLabel.style.color = def.color;
    tierLabel.textContent = def.name;
  }

  // Update rating number
  const ratingNum = el.querySelector('.rank-badge-rating-num') as HTMLElement | null;
  if (ratingNum) {
    ratingNum.textContent = String(newRating);
  }

  // Show change animation
  const changeSpan = el.querySelector('.rank-badge-change') as HTMLElement | null;
  if (changeSpan && change !== 0) {
    const isPositive = change > 0;
    changeSpan.textContent = `${isPositive ? '+' : ''}${change}`;
    changeSpan.className = `rank-badge-change ${isPositive ? 'positive' : 'negative'}`;
    changeSpan.style.display = 'inline-block';

    // Re-trigger animation by forcing reflow
    changeSpan.style.animation = 'none';
    void changeSpan.offsetHeight; // force reflow
    changeSpan.style.animation = '';

    // Hide after 3 seconds
    setTimeout(() => {
      changeSpan.style.display = 'none';
    }, 3000);
  }
}
